// API Endpoints
// Base URL is configured in .env file (VITE_API_BASE_URL)
export const RECON = {
  inventory: `/inventory/search`, // Usage: GET /inventory/search/:vin (includes sold vehicles)
  extendedInventory: (vehicleInventoryId) => `/extended_inventory/${vehicleInventoryId}`, // Usage: GET /extended_inventory/:vehicle_inventory_id
  possibleStages: `/stages`, // Usage: GET /stages, POST /stages
  paymentMethods: `/payment-methods`, // Usage: GET /payment-methods
  paymentMethod: (id) => `/payment-methods/${id}`, // Usage: PUT /payment-methods/:id, DELETE /payment-methods/:id
  deleteStage: (stageId) => `/stages/${stageId}`, // Usage: DELETE /stages/:stage_id
  updateStage: (stageId) => `/stages/${stageId}`, // Usage: PUT /stages/:stage_id
  changeReconStage: (vehicleId) => `/vehicle/change_recon_stage/${vehicleId}`, // Usage: POST /vehicle/change_recon_stage/:vehicle_id
  changeSubstatus: (vehicleInventoryId) => `/vehicle/change_substatus/${vehicleInventoryId}`, // Usage: POST /vehicle/change_substatus/:vehicle_inventory_id
  changeCustomFields: (vehicleInventoryId) => `/vehicle/change_custom_fields/${vehicleInventoryId}`, // Usage: POST /vehicle/change_custom_fields/:vehicle_inventory_id
  sendMediaToTekion: (vehicleInventoryId) => `/vehicle/add_media/${vehicleInventoryId}`, // Usage: POST /vehicle/add_media/:vehicle_inventory_id
  vehicleStage: (vehicleId) => `/vehicle/stage/${vehicleId}`, // Usage: GET /vehicle/stage/:vehicle_id
  updateVehicleStageEta: (requestId) => `/vehicle/stage/${requestId}`, // Usage: PATCH /vehicle/stage/:request_id
  validateEmail: `users/validate_email`, // Usage: GET /validate_email/:email
  generate: `users/token/generate`,
  tokenValidate: `users/token/validate`,
  userInfo: `users/token/user-info`, // Usage: POST /users/token/user-info
  userOptions: `users/options`, // Usage: GET /users/options - Returns [{email, display_name}] for active users
  inventoryPerLotAndStage: `/inventory/per_lot_and_stage`, // Usage: GET /inventory/per_lot_and_stage?lot=Santa%20Ana&stage_column=stage3&stage_value=QC%20Mechanical&sort=age:desc
  reconRequests: `/recon-requests`, // Usage: GET /recon-requests?sort=created_at:desc, POST /recon-requests
  requestsByVehicle: (vehicleInventoryId) => `/requests/vehicle/${vehicleInventoryId}`, // Usage: GET /requests/vehicle/:vehicle_inventory_id - Returns nested requests with quotes and items
  quotes: `/quotes`, // Usage: GET /quotes?sort=created_at:desc, POST /quotes
  quoteItems: `/quote-items`, // Usage: GET /quote-items, POST /quote-items
  vehicleNotes: `/vehicle-notes`, // Usage: GET /vehicle-notes?vehicle_inventory_id={id}, POST /vehicle-notes
  updateVehicleNote: (noteId) => `/vehicle-notes/${noteId}`, // Usage: PUT /vehicle-notes/:note_id
  deleteVehicleNote: (noteId) => `/vehicle-notes/${noteId}`, // Usage: DELETE /vehicle-notes/:note_id

  // Attachment endpoints (renamed from photos to support future file types)
  attachments: `/attachments`, // Usage: GET /attachments?entity_type=vehicle&entity_id={vin}, DELETE /attachments
  uploadUrl: `/attachments/upload-url`, // Usage: POST /attachments/upload-url
  confirmUpload: `/attachments/confirm-upload`, // Usage: POST /attachments/confirm-upload
  downloadUrl: `/attachments/download-url`, // Usage: POST /attachments/download-url
  presign: `/attachments/presign`, // Usage: POST /attachments/presign (upload-once: no DB row)
  attach: `/attachments/attach`, // Usage: POST /attachments/attach (register existing R2 object)

  // Update endpoints
  updateQuote: (quoteId) => `/quotes/${quoteId}`, // Usage: PUT /quotes/:quote_id
  updateQuoteItem: (itemId) => `/quote-items/${itemId}`, // Usage: PUT /quote-items/:item_id

  // History endpoints
  quoteHistory: (quoteId) => `/quotes/${quoteId}/history`, // Usage: GET /quotes/:quote_id/history
  quoteItemHistory: (itemId) => `/quote-items/${itemId}/history`, // Usage: GET /quote-items/:item_id/history
  reconRequestHistory: (requestId) => `/recon-requests/${requestId}/history`, // Usage: GET /recon-requests/:request_id/history
  auditLog: `/audit-log`, // Usage: GET /audit-log?entity_type=quote&entity_id=uuid&changed_by=user@example.com&limit=100

  // Vehicle History (unified change log)
  vehicleHistory: (vehicleInventoryId) => `/vehicles/${vehicleInventoryId}/history`, // Usage: GET /vehicles/:vehicle_inventory_id/history?entity_types=request,quote&limit=50&offset=0

  // No-Photos Inventory Report
  noPhotosSummary: `/no-photos-summary`, // Usage: GET /inventory/no-photos-summary
  noPhotosVehicles: `/no-photos-vehicles`, // Usage: GET /inventory/no-photos-vehicles?lot={lot}&sub_status={sub_status}
  noPhotosByAge: `/no-photos-by-age`, // Usage: GET /inventory/no-photos-by-age?lot={lot}&age_bucket={age_bucket}
  noPhotosMissingInfo: `/no-photos-missing-info`, // Usage: GET /inventory/no-photos-missing-info?lot={lot}
  tekionVsDealerSync: `/tekion-vs-dealersync`, // Usage: GET /inventory/tekion-vs-dealersync

  // No-Recon-Location Inventory Report
  noReconLocation: `/inventory/no-recon-location`, // Usage: GET /inventory/no-recon-location?location=Santa%20Ana,Fontana&sort=age:desc

  // Inventory Filter Report
  inventoryFilter: `/inventory/filter`, // Usage: POST /inventory/filter with body: { lots?: string[], status?: string[], sub_status?: string[], sort?: string }

  // KBB Lending Value Report
  kbbLending: `/inventory/kbb-lending`, // Usage: POST /inventory/kbb-lending with body: { lots?: string[], website_price_min?: number, website_price_max?: number, age?: number, age_max?: number, sort?: string }

  // AI Photo Description
  describePhotos: `/ai/describe-photos`,
  suggestReconRequests: `/ai/suggest-recon-requests`,
  reconSuggestionsByKeys: `/ai/recon-suggestions`, // Usage: POST { image_keys } (upload-once flow)
  vehicleNoteAiSummary: `/vehicle-notes/ai-summary`, // Usage: POST async summary note + image keys
  readInvoice: `/ai/read-invoice`,

  // AI Image Inspection Pipeline
  inspectionStatus: (runId) => `/image-export/inspect/status/${runId}`, // Usage: GET /image-export/inspect/status/:run_id
  inspectionResults: (runId) => `/image-export/inspect/results/${runId}`, // Usage: GET /image-export/inspect/results/:run_id
  inspectionHistory: (vin) => `/image-export/inspect/history/${vin}`, // Usage: GET /image-export/inspect/history/:vin
  inspectionReport: `/image-export/report`, // Usage: GET /image-export/report?limit=50&offset=0&overall_condition=POOR&has_major_issues=true
  inspectionDetail: (vehicleInventoryId) => `/image-export/report/${vehicleInventoryId}`, // Usage: GET /image-export/report/:vehicle_inventory_id
  backfillInspections: `/image-export/admin/backfill-inspections`, // Usage: POST /image-export/admin/backfill-inspections?batch_size=50

  // Customer Success
  customerSuccess: (stock) => `/customer-success/${stock}`, // Usage: GET /customer-success/:stock
  tekionEntries: (vehicleInventoryId, stock) =>
    `/customer-success/${vehicleInventoryId}/tekion-entries?stock=${encodeURIComponent(stock)}`, // Usage: GET /customer-success/:vehicle_inventory_id/tekion-entries?stock=

  // User Activity Report
  userActivitySummary: `/reports/user-activity/summary`, // Usage: GET /reports/user-activity/summary?start_date=&end_date=&lot=&user=&entity_type=
  userActivityLog: `/reports/user-activity/log`, // Usage: GET /reports/user-activity/log?start_date=&end_date=&lot=&user=&entity_type=&page=&page_size=
};

export const STAGE_CONFIG = {
  phases: `/stage-config/phases`,
  phase: (id) => `/stage-config/phases/${id}`,
  stages: `/stage-config/stages`,
  stage: (id) => `/stage-config/stages/${id}`,
  stageStatuses: (stageId) => `/stage-config/stages/${stageId}/statuses`,
  status: (id) => `/stage-config/statuses/${id}`,
};

export const SAVED_REPORTS = {
  list: `/saved-reports`,
  listAll: `/saved-reports/all`,
  create: `/saved-reports`,
  get: (id) => `/saved-reports/${id}`,
  update: (id) => `/saved-reports/${id}`,
  delete: (id) => `/saved-reports/${id}`,

};

export const USER_PREFERENCES = {
  get: (key) => `/users/me/preferences/${key}`, // Usage: GET
  upsert: (key) => `/users/me/preferences/${key}`, // Usage: PUT
  delete: (key) => `/users/me/preferences/${key}`, // Usage: DELETE
};

export const SYNDICATION = {
  comments: `/syndication/comments`,
  comment: (vehicleInventoryId) => `/syndication/comments/${vehicleInventoryId}`,
  onWebsite: (vehicleInventoryId) => `/syndication/comments/${vehicleInventoryId}/on-website`,
};

export const ADMIN = {
  users: `/admin/users`, // Usage: GET /admin/users?offset=&limit=&access_level=&is_active=&search=, POST /admin/users
  user: (id) => `/admin/users/${id}`, // Usage: GET /admin/users/:id, PATCH /admin/users/:id, DELETE /admin/users/:id
  roles: `/admin/roles`, // Usage: GET /admin/roles, POST /admin/roles
  role: (id) => `/admin/roles/${id}`, // Usage: PATCH /admin/roles/:id
  rolePermissions: (id) => `/admin/roles/${id}/permissions`, // Usage: GET/PUT /admin/roles/:id/permissions
  me: `users/me`, // Usage: GET /users/me
};

export const API_RESPONSE = {
  SUCCESS: 1,
  FAILURE: 0,
};
